import React, { useRef, useEffect, useContext, createContext } from 'react';
import { View, Text, StyleSheet, Platform } from 'react-native';
import { useTheme } from '../../contexts/ThemeContext';
import { createStyles } from '../../shared/utils/styles';
import { DesignTokens } from '../../shared/types/tokens';
import { useResponsive } from '../../shared/utils/responsive';
import { CommentContextType } from '../../shared/types/comments';
import { CommentThread } from './CommentThread';
import { useFocusEffect } from 'expo-router';
import { PageContainerRef } from '../layout/PageContainer';

interface CommentSectionProps {
  contextType: CommentContextType;
  contextId: number;
  title?: string;
  autoCreateThread?: boolean;
  defaultThreadTitle?: string;
  compact?: boolean; // For inline/smaller displays
  threadId?: number | null; // If provided, show specific thread and scroll to it
}

/**
 * Reusable CommentSection component that wraps CommentThread
 * with consistent styling and responsive design
 * 
 * Usage:
 * <CommentSection
 *   contextType="property"
 *   contextId={property.id}
 *   title="Property Discussion"
 *   autoCreateThread={true}
 * />
 */
export function CommentSection({
  contextType,
  contextId,
  title,
  autoCreateThread = false,
  defaultThreadTitle = 'General Discussion',
  compact = false,
  threadId,
}: CommentSectionProps) {
  const { tokens } = useTheme();
  const utils = createStyles(tokens);
  const responsive = useResponsive();
  const { isMobile, isTablet, isDesktop } = responsive;
  const styles = makeStyles(tokens, utils, { isMobile, isTablet, isDesktop, compact });
  const containerRef = useRef<View>(null);
  const sectionId = `comment-section-${contextType}-${contextId}`;

  // Scroll to comment input area when threadId is provided (e.g., when navigating from discussions)
  useFocusEffect(
    React.useCallback(() => {
      if (threadId) {
        // Use a retry mechanism to find the element
        const scrollToInput = () => {
          if (Platform.OS === 'web') {
            // Web: Find the comment input element by ID and scroll to it
            const inputElement = document.getElementById(`comment-input-${threadId}`);
            if (inputElement) {
              // Wait a bit more for layout to settle
              setTimeout(() => {
                const elementTop = inputElement.getBoundingClientRect().top + window.pageYOffset;
                window.scrollTo({ top: elementTop - 150, behavior: 'smooth' });
              }, 100);
              return true;
            }
            
            // Fallback: try to find any comment input with data attribute
            const fallbackInput = document.querySelector('[data-comment-input="true"]') as HTMLElement;
            if (fallbackInput) {
              setTimeout(() => {
                const elementTop = fallbackInput.getBoundingClientRect().top + window.pageYOffset;
                window.scrollTo({ top: elementTop - 150, behavior: 'smooth' });
              }, 100);
              return true;
            }
            
            // Last fallback: scroll to the section and estimate where input would be
            const element = document.getElementById(sectionId);
            if (element) {
              setTimeout(() => {
                const elementRect = element.getBoundingClientRect();
                const elementBottom = elementRect.bottom + window.pageYOffset;
                // Scroll to near the bottom of the section (where input should be)
                window.scrollTo({ top: elementBottom - 300, behavior: 'smooth' });
              }, 100);
              return true;
            }
            
            return false;
          } else {
            // Native: The CommentThread component will handle scrolling internally
            return true;
          }
        };

        // Try immediately, then retry with increasing delays
        let attempts = 0;
        const maxAttempts = 10;
        const attemptScroll = () => {
          attempts++;
          if (scrollToInput() || attempts >= maxAttempts) {
            return;
          }
          // Retry with exponential backoff
          setTimeout(attemptScroll, 200 * attempts);
        };

        // Start attempting after initial delay
        const timer = setTimeout(() => {
          attemptScroll();
        }, 500);
        
        return () => clearTimeout(timer);
      }
    }, [threadId, sectionId])
  );

  return (
    <View 
      ref={containerRef} 
      style={styles.container}
      {...(Platform.OS === 'web' ? { id: sectionId } : {})}
      testID={sectionId}
    >
      {title && (
        <View style={styles.header}>
          <Text style={styles.title}>{title}</Text>
        </View>
      )}
      <View style={styles.threadContainer}>
        <CommentThread
          contextType={contextType}
          contextId={contextId}
          threadId={threadId}
          autoCreateThread={autoCreateThread}
          defaultThreadTitle={defaultThreadTitle}
        />
      </View>
    </View>
  );
}

function makeStyles(
  tokens: DesignTokens,
  utils: ReturnType<typeof createStyles>,
  responsive: { isMobile: boolean; isTablet: boolean; isDesktop: boolean; compact: boolean }
) {
  const { isMobile, isTablet, isDesktop, compact } = responsive;
  
  return StyleSheet.create({
    container: {
      width: '100%',
      marginBottom: compact ? utils.spacing.md : utils.spacing.xl,
      paddingBottom: isMobile ? utils.spacing.xl : 0,
    },
    header: {
      marginBottom: utils.spacing.md,
      paddingHorizontal: isDesktop ? utils.spacing.xl : (isMobile ? utils.spacing.md : utils.spacing.lg),
    },
    title: {
      fontSize: isMobile ? utils.typography.sizes.lg : utils.typography.sizes.xl,
      fontWeight: utils.typography.weights.semibold,
      color: tokens.color.onSurface,
      fontFamily: utils.typography.body.fontFamily,
    },
    threadContainer: {
      backgroundColor: tokens.color.surface,
      borderRadius: utils.radius.lg,
      borderWidth: 1,
      borderColor: tokens.color.border,
      overflow: 'hidden',
      // On mobile, make the container taller to be more usable
      minHeight: isMobile ? 500 : compact ? 300 : 450,
      // Allow the container to grow to fill available space
      flex: compact ? 0 : 1,
      ...(compact && {
        maxHeight: isMobile ? 500 : 550,
      }),
    },
  });
}
